import {
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	InfoCircle,
} from "@untitledui/icons";
import { FC, ReactNode, useMemo } from "react";
import { useNavigate } from "react-router";

import type { FeaturedIconProps } from "@/assets/icons/featured-icon/featured-icon";

import { BackgroundPatternProps } from "@/assets/background-patterns";
import { ROUTES } from "@/constants/routes";
import { cx } from "@/utils/cx";

import { EmptyState } from "../empty-state/empty-state";
import { Button } from "../ui/buttons/button";

type Navigate =
	| true
	| {
			path: string;
			text: string;
	  };

interface StatusMessageProps {
	backButton?: boolean;
	className?: string;
	customButton?: () => void;
	customButtonText?: string;
	description?: ReactNode;
	fullHeight?: boolean;
	icon?: FC<{ className?: string }> | ReactNode;
	iconSize?: "lg" | "md" | "sm" | "xl";
	includeCallToAction?: boolean;
	navigate?: Navigate;
	pattern?: "none" | BackgroundPatternProps["pattern"];
	refetch?: () => void;
	refetchText?: string;
	size?: "lg" | "md" | "sm";
	theme?: FeaturedIconProps["theme"];
	title?: string;
	type?: "error" | "gray" | "info" | "success" | "warning";
}

export const StatusMessage = ({
	className,
	customButton,
	customButtonText,
	description,
	fullHeight = true,
	icon,
	iconSize = "xl",
	includeCallToAction = false,
	navigate,
	pattern = "circle",
	refetch,
	refetchText = "Try Again",
	size = "lg",
	theme = "modern",
	title,
	type = "error",
}: StatusMessageProps) => {
	const routerNavigate = useNavigate();
	const defaultDescription = useMemo(() => {
		switch (type) {
			case "error":
				return "There was an error loading this page. Please try again later.";
			default:
				return null;
		}
	}, [type]);

	const defaultTitle = useMemo(() => {
		switch (type) {
			case "error":
				return "Error";
			default:
				return null;
		}
	}, [type]);

	// Sets the default icon based on the type
	const defaultIcon = useMemo(() => {
		switch (type) {
			case "error":
				return <AlertTriangle />;
			case "info":
				return <InfoCircle />;
			case "success":
				return <CheckCircle />;
			case "warning":
				return <AlertCircle />;
			default:
				return null;
		}
	}, [type]);

	// Sets the default icon color based on the type. Note the icons color only applied when the theme isn't "modern" or "modern-neue"
	const iconColor: FeaturedIconProps["color"] = useMemo(() => {
		switch (type) {
			case "error":
				return "error";
			case "info":
				return "gray";
			case "success":
				return "success";
			case "warning":
				return "warning";
			default:
				return "gray";
		}
	}, [type]);

	const CallToAction = includeCallToAction ? (
		<EmptyState.Description>
			If the problem persists, please{" "}
			<Button color="link-color" href={ROUTES.SUPPORT} size="lg">
				contact support
			</Button>{" "}
			for help.
		</EmptyState.Description>
	) : null;

	const CustomButton =
		customButton && customButtonText ? (
			<Button color="primary" onClick={customButton} size="md">
				{customButtonText}
			</Button>
		) : null;

	// Used to refetch the data when the user clicks the button if the refetch function is provided
	const RefetchButton = refetch ? (
		<Button color="secondary" onClick={refetch} size="md">
			{refetchText}
		</Button>
	) : null;

	// Set the navigate path based on the navigate prop
	const navigatePath =
		typeof navigate === "object" && "path" in navigate
			? navigate.path
			: undefined;

	// Set the navigate button text based on the navigate prop
	const navigateText =
		typeof navigate === "object" && "text" in navigate
			? navigate.text
			: "Go Back";

	// Used to navigate to the previous page when the user clicks the button if the navigate prop is provided or the path is provided
	const NavigateButton = navigate ? (
		<Button
			color="primary"
			onClick={() =>
				navigatePath ? routerNavigate(navigatePath) : routerNavigate(-1)
			}
			size="md"
		>
			{navigateText}
		</Button>
	) : null;

	return (
		<EmptyState
			className={cx(
				"max-h-(--page-content-height) w-full overflow-hidden p-8 text-center",
				fullHeight ? "h-lg:h-screen" : "h-auto",
				className,
			)}
			size={size}
		>
			<EmptyState.Header pattern={pattern}>
				<EmptyState.FeaturedIcon
					color={iconColor}
					icon={icon || defaultIcon}
					size={iconSize}
					theme={theme}
				/>
			</EmptyState.Header>
			<EmptyState.Content>
				<EmptyState.Title>{title || defaultTitle}</EmptyState.Title>
				<EmptyState.Description>
					{description || defaultDescription}
				</EmptyState.Description>
				{includeCallToAction && CallToAction}
				{refetch || navigate || customButton ? (
					<div className="mt-2 flex gap-2">
						{refetch && RefetchButton}
						{navigate && NavigateButton}
						{customButton && CustomButton}
					</div>
				) : null}
			</EmptyState.Content>
		</EmptyState>
	);
};
